import React, { JSX, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CellProps } from 'react-table';
import { InfoIcon } from '@chakra-ui/icons';
import { Button, Flex, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import { Table } from 'common/components';
import { useTranslations } from 'contexts/LocalizationContext';

import { TABLE_CELL_EMPTY_VALUE } from '../../../../common/components/Table/utils/table.const';
import { DEFAULT_PAGINATED_TABLE_LIMIT } from '../../../../common/constants/common';
import { getLocalDateTimeByUtcTimestamp, useExecuteWithConfirmation } from '../../../../common/utils';
import { convertFeeInMinUnitToMainUnitFixed } from '../../../../common/utils/fee.helper';
import { useCancelUserDebt, useGetUserDebts } from '../../../../services/application-users';
import { AssignedItemResponse } from '../model/assigned-item.response';
import { DebtTableStatus } from '../model/enums/debt-status.enum';
import { PaymentHistoryResponse } from '../model/payment-history.response';
import { UserDebtItemResponse } from '../model/user-debt-item.response';
import { getItemTableCell, mapAssignedItemToEcoFeeReason } from '../utils/eco-fee.helper';
import { buildDebtStatusOptions } from '../utils/status-options.const';
import { BorrowingDetailsModal } from './BorrowingDetailsModal';
import { DebtPaymentModal } from './DebtPaymentModal';
import { DebtStatus } from './DebtStatus';
import { StatusFilterSelect } from './StatusFilterSelect';

const DebtsTable = (): JSX.Element => {
  const { userId } = useParams<{ userId: string }>();
  const [page, setPage] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<'all' | DebtTableStatus>('all');
  const { data, isFetching: isFetchingDebts } = useGetUserDebts(userId ?? null, {
    searchText: '',
    limit: DEFAULT_PAGINATED_TABLE_LIMIT,
    page,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });
  const borrowingDetailsModal = useDisclosure();
  const debtPaymentModal = useDisclosure();

  const [selectedAssignedItem, setSelectedAssignedItem] = useState<AssignedItemResponse | null>(null);
  const [selectedDebtPayment, setSelectedDebtPayment] = useState<PaymentHistoryResponse[]>([]);

  const { mutate: cancelUserDebt, isLoading: isCancellingDebt } = useCancelUserDebt();
  const t = useTranslations();
  const { executeWithConfirmation: confirmCancelDebt } = useExecuteWithConfirmation<{ debtId: string }>(
    t('users_eco_fee.debts.cancel_confirm'),
    ({ debtId }) => {
      if (!userId) return;
      cancelUserDebt({ userId, debtId });
    },
  );

  const handleOpenAssignedItemModal = useCallback(
    (row: AssignedItemResponse) => () => {
      setSelectedAssignedItem(row);
      borrowingDetailsModal.onOpen();
    },
    [borrowingDetailsModal],
  );

  const handleCloseAssignedItemModal = useCallback(() => {
    setSelectedAssignedItem(null);
    borrowingDetailsModal.onClose();
  }, [borrowingDetailsModal]);

  const handleOpenDebtPaymentModal = useCallback(
    (rows: PaymentHistoryResponse[]) => () => {
      setSelectedDebtPayment(rows);
      debtPaymentModal.onOpen();
    },
    [debtPaymentModal],
  );

  const handleCloseDebtPaymentModal = useCallback(() => {
    setSelectedDebtPayment([]);
    debtPaymentModal.onClose();
  }, [debtPaymentModal]);

  const handleStatusFilterChange = useCallback((val: string) => {
    setPage(1);
    setStatusFilter(val as 'all' | DebtTableStatus);
  }, []);

  const handleCancelDebt = useCallback(
    (original: UserDebtItemResponse) => {
      return () => confirmCancelDebt({ debtId: original.id });
    },
    [confirmCancelDebt],
  );

  const columns = useMemo(
    () => [
      {
        id: 'id',
        Header: t('users_eco_fee.debts.table.debt_id'),
        accessor: 'id' as const,
      },
      {
        id: 'amount',
        Header: t('users_eco_fee.common.amount'),
        textAlign: 'right',
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<UserDebtItemResponse>) => (
          <>
            {convertFeeInMinUnitToMainUnitFixed(original.amount)} {original.currency}
          </>
        ),
      },
      {
        id: 'creationTimestamp',
        Header: t('users_eco_fee.debts.table.created_at'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<UserDebtItemResponse>) => <>{getLocalDateTimeByUtcTimestamp(original.creationTimestamp)}</>,
      },
      {
        id: 'item',
        Header: t('users_eco_fee.common.item'),
        textAlign: 'center',
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<UserDebtItemResponse>) => <Text>{getItemTableCell(original.assignedItem.product, t)}</Text>,
      },
      {
        id: 'reason',
        Header: t('users_eco_fee.common.reason'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<UserDebtItemResponse>) => (
          <Flex justifyContent={'center'} alignItems={'center'} align="center" gap={2} maxW="380px">
            <Text flex="1 1 auto">{mapAssignedItemToEcoFeeReason(original.assignedItem, t)}</Text>
            <IconButton
              aria-label={t('users_eco_fee.common.details')}
              size="md"
              variant={'ghost'}
              onClick={handleOpenAssignedItemModal(original.assignedItem)}
              icon={<InfoIcon color={'blueGrey.800'} />}
            ></IconButton>
          </Flex>
        ),
      },
      {
        id: 'status',
        Header: t('users_eco_fee.common.status'),
        textAlign: 'center',
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<UserDebtItemResponse>) => <DebtStatus debt={original} />,
      },
      {
        id: 'payment',
        Header: t('users_eco_fee.common.payment'),
        textAlign: 'center',
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<UserDebtItemResponse>) =>
          original.paymentAttempts.length ? (
            <Button variant="outline" size="sm" onClick={handleOpenDebtPaymentModal(original.paymentAttempts)}>
              {t('users_eco_fee.common.details')}
            </Button>
          ) : (
            <>{t('users_eco_fee.debts.table.no_payment_method')}</>
          ),
      },
      {
        id: 'actions',
        Header: t('users_eco_fee.common.actions'),
        Cell: ({
          cell: {
            row: { original },
          },
        }: CellProps<UserDebtItemResponse>) => {
          if (original.cancellation?.timestamp) {
            return <>{TABLE_CELL_EMPTY_VALUE}</>;
          } else {
            return (
              <Button size="sm" variant={'critical'} onClick={handleCancelDebt(original)} isDisabled={isCancellingDebt}>
                {t('users_eco_fee.debts.cancel_eco_fee')}
              </Button>
            );
          }
        },
      },
    ],
    [t, handleOpenAssignedItemModal, handleOpenDebtPaymentModal, handleCancelDebt, isCancellingDebt],
  );

  return (
    <>
      <Table<UserDebtItemResponse>
        data={data?.items ?? []}
        columns={columns}
        isLoading={isFetchingDebts}
        paginationEnabled
        page={page}
        onPageChanged={setPage}
        totalPages={data?.totalPages}
      >
        <StatusFilterSelect
          label={t('users_eco_fee.common.status') + ':'}
          value={statusFilter}
          onChange={handleStatusFilterChange}
          options={buildDebtStatusOptions(t)}
        />
      </Table>
      <BorrowingDetailsModal
        isOpen={borrowingDetailsModal.isOpen}
        onClose={handleCloseAssignedItemModal}
        items={selectedAssignedItem ? [selectedAssignedItem] : []}
      />
      <DebtPaymentModal
        payments={selectedDebtPayment}
        isOpen={debtPaymentModal.isOpen}
        onClose={handleCloseDebtPaymentModal}
      />
    </>
  );
};

export default DebtsTable;
